import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MessageSquare, ShoppingCart, ChevronLeft, ChevronRight, X, ZoomIn,
  Share2, Copy, Check, Facebook, Linkedin, Mail
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social share icons (custom SVGs for platforms not in lucide)
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XTwitterIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M12 0a12 12 0 0 0-4.373 23.178c-.07-.937-.134-2.377.028-3.401.147-.928.948-5.908.948-5.908s-.242-.484-.242-1.2c0-1.124.652-1.963 1.464-1.963.69 0 1.023.518 1.023 1.14 0 .695-.443 1.734-.671 2.698-.19.804.404 1.46 1.198 1.46 1.438 0 2.543-1.516 2.543-3.702 0-1.935-1.39-3.289-3.377-3.289-2.301 0-3.653 1.726-3.653 3.511 0 .695.268 1.44.603 1.844.066.08.076.15.056.232-.062.256-.199.804-.226.916-.036.148-.119.18-.275.108-1.027-.478-1.67-1.98-1.67-3.187 0-2.593 1.884-4.975 5.433-4.975 2.852 0 5.068 2.032 5.068 4.749 0 2.833-1.786 5.113-4.265 5.113-.833 0-1.617-.433-1.885-.944l-.512 1.954c-.186.715-.689 1.612-1.025 2.16A12 12 0 1 0 12 0z"/>
  </svg>
);

const Shop = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  
  // Product detail modal state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  
  // Share state
  const [linkCopied, setLinkCopied] = useState(false);

  // Autoplay plugin with stop on interaction
  const autoplayPlugin = React.useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  // Embla Carousel setup with autoplay
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [autoplayPlugin.current]
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle shared product link - open modal when ?product=ID is in URL
  useEffect(() => {
    const productId = searchParams.get('product');
    if (productId && products.length > 0) {
      const product = products.find(p => p._id === productId);
      if (product) {
        openProductModal(product);
        // Clear the URL parameter after opening
        setSearchParams({});
      }
    }
  }, [products, searchParams]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Sort by createdAt descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setProducts(sorted);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['All', ...new Set(products.flatMap(p => p.categories))];
  
  const filteredProducts = filter === 'All' 
    ? products 
    : products.filter(product => product.categories.includes(filter));

  // Products for carousel (sorted by date, newest first)
  const carouselProducts = products.filter(p => p.status === 'active');

  const handleRequestQuote = (product) => {
    setProductModalOpen(false);
    navigate('/contact', { state: { preselectedService: product.name } });
    window.scrollTo(0, 0);
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product._id,
      name: product.name,
      price: product.price,
      image: product.images?.[0],
      quantity: 1
    });
    toast({
      title: "Added to Cart",
      description: `${product.name} has been added to your cart.`
    });
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setSelectedImageIndex(0);
    setProductModalOpen(true);
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
  };

  // Share functions
  const getProductShareUrl = (product) => {
    // Create shareable URL with product slug/id
    const baseUrl = window.location.origin;
    return `${baseUrl}/shop?product=${product._id}`;
  };

  const getShareText = (product) => {
    const price = product.priceType === 'fixed' ? `$${product.price}` : 'Get a quote';
    return `Check out ${product.name} - ${price}! ${product.description?.substring(0, 100)}...`;
  };

  const copyProductLink = async (product) => {
    const url = getProductShareUrl(product);
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast({
        title: "Link Copied!",
        description: "Product link copied to clipboard"
      });
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      toast({
        title: "Link Copied!",
        description: "Product link copied to clipboard"
      });
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const shareOnWhatsApp = (product) => {
    const url = getProductShareUrl(product);
    const text = getShareText(product);
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n\n' + url)}`, '_blank');
  };

  const shareOnFacebook = (product) => {
    const url = getProductShareUrl(product);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnTwitter = (product) => {
    const url = getProductShareUrl(product);
    const text = getShareText(product);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnLinkedIn = (product) => {
    const url = getProductShareUrl(product);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareOnPinterest = (product) => {
    const url = getProductShareUrl(product);
    const image = product.images?.[0] || '';
    const description = getShareText(product);
    window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(description)}`, '_blank');
  };

  const shareByEmail = (product) => {
    const url = getProductShareUrl(product);
    const subject = `Check out: ${product.name}`;
    const body = `${getShareText(product)}\n\nView product: ${url}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  return (
    <div className="bg-black text-white pt-24">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our <span className="text-[#F4B400]">Services</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Professional creative services tailored to your needs
            </p>
          </div>
        </div>
      </section>

      {/* Products Carousel - Auto-scrolling */}
      {carouselProducts.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-gray-900 to-black">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-bold">Featured Products & Services</h2>
                <p className="text-gray-400 mt-2">Browse our latest offerings (newest first)</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={scrollPrev}
                  variant="outline"
                  size="icon"
                  className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
                >
                  <ChevronLeft size={24} />
                </Button>
                <Button
                  onClick={scrollNext}
                  variant="outline"
                  size="icon"
                  className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
                >
                  <ChevronRight size={24} />
                </Button>
              </div>
            </div>

            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex gap-6">
                {carouselProducts.map((product) => (
                  <div 
                    key={product._id} 
                    className="flex-none w-[280px] md:w-[320px]"
                  >
                    <Card 
                      className="bg-white text-black h-full overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                      onClick={() => openProductModal(product)}
                    >
                      <div className="relative overflow-hidden">
                        <img 
                          src={product.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'} 
                          alt={product.name}
                          className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        {product.featured && (
                          <Badge className="absolute top-2 left-2 bg-[#F4B400] text-black">NEW</Badge>
                        )}
                        {product.price > 0 && (
                          <Badge className="absolute top-2 right-2 bg-green-500 text-white">
                            From ${product.price}
                          </Badge>
                        )}
                        {/* Zoom indicator */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                          <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={32} />
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-bold text-lg mb-2 line-clamp-1">{product.name}</h3>
                        <p className="text-sm text-gray-500 mb-3 line-clamp-2">{product.description}</p>
                        
                        {/* Show buttons based on price and priceType */}
                        <div className="space-y-2">
                          {product.price > 0 && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                              className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00]"
                              size="sm"
                            >
                              <ShoppingCart className="mr-2" size={16} />
                              Add to Cart
                            </Button>
                          )}
                          {(product.priceType === 'quote' || !product.priceType) && (
                            <Button
                              onClick={(e) => { e.stopPropagation(); handleRequestQuote(product); }}
                              variant="outline"
                              className="w-full border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
                              size="sm"
                            >
                              <MessageSquare className="mr-2" size={16} />
                              Get Quote
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filter */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant={filter === category ? 'default' : 'outline'}
                className={
                  filter === category
                    ? 'bg-[#F4B400] text-black hover:bg-[#d49f00]'
                    : 'border-gray-300 text-black hover:border-[#F4B400] hover:text-[#F4B400]'
                }
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Services/Products Grid */}
      <section className="py-16 bg-white text-black">
        <div className="container mx-auto px-6">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading services...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <Card 
                  key={product._id}
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#F4B400] cursor-pointer"
                  onClick={() => openProductModal(product)}
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={product.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'} 
                      alt={product.name}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {product.price > 0 && (
                      <Badge className="absolute top-4 right-4 bg-green-500 text-white text-lg px-3 py-1">
                        From ${product.price}
                      </Badge>
                    )}
                    {/* Zoom indicator */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={40} />
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold mb-3">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2">{product.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {product.categories?.map((cat, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="mt-6 space-y-3">
                      {/* Show price if available */}
                      {product.price > 0 ? (
                        <p className="text-3xl font-bold text-green-600">From ${product.price}</p>
                      ) : (
                        <p className="text-2xl font-bold text-[#F4B400]">Price upon quotation</p>
                      )}
                      
                      {/* Add to Cart button - show if has price */}
                      {product.price > 0 && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                          className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] transition-all duration-300"
                        >
                          <ShoppingCart className="mr-2" size={18} />
                          Add to Cart
                        </Button>
                      )}
                      
                      {/* Request Quote button - show if priceType is quote or undefined */}
                      {(product.priceType === 'quote' || !product.priceType) && (
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleRequestQuote(product); }}
                          className="w-full bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black transition-all duration-300"
                        >
                          <MessageSquare className="mr-2" size={18} />
                          Request a Quote
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {filteredProducts.length === 0 && !loading && (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg">No services found in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Need a Custom Solution?</h2>
            <p className="text-gray-300 text-lg mb-8">
              Every project is unique. Get in touch for a personalized quote tailored to your needs.
            </p>
            <Button
              onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}
              size="lg"
              className="bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg px-8 py-6"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Product Detail Modal */}
      <Dialog open={productModalOpen} onOpenChange={setProductModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white text-black p-0">
          {selectedProduct && (
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image Gallery */}
              <div className="bg-gray-100 p-6">
                {/* Main Image */}
                <div 
                  className="relative group cursor-pointer mb-4"
                  onClick={() => openLightbox(selectedProduct.images?.[selectedImageIndex] || selectedProduct.images?.[0])}
                >
                  <img 
                    src={selectedProduct.images?.[selectedImageIndex] || selectedProduct.images?.[0] || 'https://via.placeholder.com/400x400?text=No+Image'} 
                    alt={selectedProduct.name}
                    className="w-full h-80 object-cover rounded-lg shadow-lg transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={40} />
                  </div>
                  {selectedProduct.priceType === 'fixed' && (
                    <Badge className="absolute top-4 right-4 bg-green-500 text-white text-xl px-4 py-2">
                      ${selectedProduct.price}
                    </Badge>
                  )}
                </div>
                
                {/* Thumbnail Gallery */}
                {selectedProduct.images && selectedProduct.images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {selectedProduct.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedImageIndex === idx 
                            ? 'border-[#F4B400] shadow-lg' 
                            : 'border-transparent hover:border-gray-300'
                        }`}
                      >
                        <img 
                          src={img} 
                          alt={`${selectedProduct.name} ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
                
                <p className="text-center text-sm text-gray-500 mt-3">
                  Click image to view full size
                </p>
              </div>
              
              {/* Product Info */}
              <div className="p-6 flex flex-col">
                <div className="flex-grow">
                  <h2 className="text-3xl font-bold mb-4">{selectedProduct.name}</h2>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedProduct.categories?.map((cat, idx) => (
                      <Badge key={idx} variant="outline" className="text-sm">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {selectedProduct.description}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    {selectedProduct.price > 0 ? (
                      <p className="text-4xl font-bold text-green-600">From ${selectedProduct.price}</p>
                    ) : (
                      <p className="text-2xl font-bold text-[#F4B400]">Price upon quotation</p>
                    )}
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3 pt-4 border-t">
                  {/* Add to Cart - show if has price */}
                  {selectedProduct.price > 0 && (
                    <Button
                      onClick={() => { handleAddToCart(selectedProduct); setProductModalOpen(false); }}
                      className="w-full bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg py-6"
                    >
                      <ShoppingCart className="mr-2" size={20} />
                      Add to Cart
                    </Button>
                  )}
                  
                  {/* Request Quote - show if priceType is quote or undefined */}
                  {(selectedProduct.priceType === 'quote' || !selectedProduct.priceType) && (
                    <Button
                      onClick={() => handleRequestQuote(selectedProduct)}
                      variant={selectedProduct.price > 0 ? "outline" : "default"}
                      className={selectedProduct.price > 0 
                        ? "w-full border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black text-lg py-6"
                        : "w-full bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg py-6"
                      }
                    >
                      <MessageSquare className="mr-2" size={20} />
                      Request a Quote
                    </Button>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setProductModalOpen(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Continue Shopping
                    </Button>
                    
                    {/* Share Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="px-4 border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black"
                        >
                          <Share2 size={20} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem 
                          onClick={() => copyProductLink(selectedProduct)}
                          className="cursor-pointer"
                        >
                          {linkCopied ? (
                            <>
                              <Check className="mr-2 h-4 w-4 text-green-500" />
                              Link Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="mr-2 h-4 w-4" />
                              Copy Link
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => shareOnWhatsApp(selectedProduct)}
                          className="cursor-pointer"
                        >
                          <span className="mr-2 text-green-500"><WhatsAppIcon /></span>
                          WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => shareOnFacebook(selectedProduct)}
                          className="cursor-pointer"
                        >
                          <Facebook className="mr-2 h-4 w-4 text-blue-600" />
                          Facebook
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => shareOnTwitter(selectedProduct)}
                          className="cursor-pointer"
                        >
                          <span className="mr-2"><XTwitterIcon /></span>
                          X (Twitter)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => shareOnLinkedIn(selectedProduct)}
                          className="cursor-pointer"
                        >
                          <Linkedin className="mr-2 h-4 w-4 text-blue-700" />
                          LinkedIn
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => shareOnPinterest(selectedProduct)}
                          className="cursor-pointer"
                        >
                          <span className="mr-2 text-red-600"><PinterestIcon /></span>
                          Pinterest
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => shareByEmail(selectedProduct)}
                          className="cursor-pointer"
                        >
                          <Mail className="mr-2 h-4 w-4" />
                          Email
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-[#F4B400] transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>
          
          <img 
            src={lightboxImage} 
            alt="Full size view"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Navigation arrows for multiple images */}
          {selectedProduct?.images && selectedProduct.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : selectedProduct.images.length - 1;
                  setSelectedImageIndex(newIndex);
                  setLightboxImage(selectedProduct.images[newIndex]);
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-[#F4B400] hover:text-black p-3 rounded-full transition-all"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const newIndex = selectedImageIndex < selectedProduct.images.length - 1 ? selectedImageIndex + 1 : 0;
                  setSelectedImageIndex(newIndex);
                  setLightboxImage(selectedProduct.images[newIndex]);
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white hover:bg-[#F4B400] hover:text-black p-3 rounded-full transition-all"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
          
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-sm">
            {selectedProduct?.images?.length > 1 
              ? `Image ${selectedImageIndex + 1} of ${selectedProduct.images.length} • Click outside or press ESC to close`
              : 'Click outside or press ESC to close'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default Shop;
